import { notification } from 'antd'
import ErrorMessageModal from 'components/Modals/ErrorMessageModal'
import ReInvestStepper from 'components/ReInvestStepper'
import { useNorthCapitalApi } from 'hooks/useNorthCapitalAPI'
import usePayments from 'hooks/usePayments'
import React, { useEffect, useState } from 'react'
import ReactGA from 'react-ga4'
import { AiFillCheckCircle, AiFillCloseCircle } from 'react-icons/ai'
import { LuLoader2 } from 'react-icons/lu'
import { consoleLog } from 'utils/simpleLogger'
import CompanyLogo from '../../../assets/afbc.png'
import BankAccountIcon from '../../../assets/bank-account.png'
import BankWireIcon from '../../../assets/bank-wire.png'
import CreditDebitIcon from '../../../assets/credit.png'
import Side2 from '../../../assets/investing-signup-images/refilling-beer.jpg'
import Button from '../../../components/Button'
import { useRiseUpAPIV1 } from '../../../hooks/useRiseUpAPIV1'
import { IFormProps } from '../../../interfaces'

export enum PaymentMethodsEnum {
  ACH = 'ACH',
  CARD = 'CARD',
  WIRE = 'WIRE',
}

const PaymentSelection = (props: IFormProps) => {
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [selected, setSelected] = useState<PaymentMethodsEnum | null>(
    props.getValues('investingProfile.paymentMethod') in PaymentMethodsEnum
      ? (props.getValues('investingProfile.paymentMethod') as PaymentMethodsEnum)
      : null,
  )
  const [samePaymentMethod, setSamePaymentMethod] = useState(true)
  const { getOnboardProgress } = useRiseUpAPIV1()
  const { createTrade, addCard, linkExternalAccount, getLinkedCreditCard, getLinkedExternalAccount } =
    useNorthCapitalApi()
  const { initiateCCPayment } = usePayments()

  const getFreshBearerToken = async () => {
    const getOnboardProgressRes = await getOnboardProgress(props.getValues('onboardingId'))
    if (getOnboardProgressRes.status === 200) {
      return getOnboardProgressRes.data.accessToken
    }
  }

  const handlePaymentSelection = (value: PaymentMethodsEnum) => {
    setSelected(value)
    props.setPaymentOptionSelected(value)
    props.setValue('investingProfile.paymentMethod', value)
  }

  const onProceed = async () => {
    props.nextStep('5')
  }

  const onError = async (error: any) => {
    ReactGA.event({
      category: 'Payment selection page',
      action: `Payment selection page error`,
      label: JSON.stringify(error),
    })
    setIsLoading(false)
    setError(error.response.data._error || 'Unexpected Error')
  }

  const handleCCPaymentMethod = async (accessToken: string) => {
    let getLinkedCreditCardRes = await getLinkedCreditCard(accessToken)
    consoleLog('CC geting card link card info:', getLinkedCreditCardRes)

    if (getLinkedCreditCardRes.data.statusDesc === 'No Credit Card Linked') {
      let addCardRes = await addCard(
        props.getValues('investingProfile.accountId'),
        props.getValues('investingProfile.investorId'),
        accessToken,
      )
      consoleLog('CC adding card response:', addCardRes)

      props.setValue('paymentIframe', addCardRes.data.accountDetails)
      onProceed()
    } else {
      props.setValue('investingProfile.accountId', getLinkedCreditCardRes.data.creditcardDetails.accountId as string)
      props.setValue('ccDetails', getLinkedCreditCardRes.data?.creditcardDetails)
      props.unregister('paymentIframe')
      onProceed()
    }
  }

  const handleACHPaymentMethod = async (accessToken: string) => {
    // check if investor has linkedExternalAccount
    let getLinkedExternalAccountRes = await getLinkedExternalAccount(accessToken)
    consoleLog('Get external link account response:', getLinkedExternalAccountRes)

    if (getLinkedExternalAccountRes && getLinkedExternalAccountRes?.data?.statusCode === '101') {
      // setNickname in form state
      props.setValue('accountNickname', getLinkedExternalAccountRes?.data?.statusDesc?.AccountNickName)
      props.setValue('achDetails', getLinkedExternalAccountRes?.data?.statusDesc)
      onProceed()
    } else {
      const res = await linkExternalAccount(accessToken)
      props.setValue('paymentIframe', res.data.accountDetails)
      props.setValue('achDetails', res.data.accountDetails)
      onProceed()
    }
  }

  const handleWirePaymentMethod = async (accessToken: string) => {
    consoleLog('Creating trade...')
    const tradeRes = await createTrade(
      props.getValues('investingProfile.accountId'),
      'WIRE',
      props.getValues('investingProfile.totalShares'),
      accessToken,
    )

    const tradeId = tradeRes.data.purchaseDetails[1][0].tradeId
    props.setValue('investingProfile.tradeId', tradeId)

    onProceed()
  }
  const handlePaymentMethod = async (accessToken: string) => {
    consoleLog('Initializing iframe link')

    const paymentMethod = samePaymentMethod
      ? props.getValues('investingProfile.previousPaymentMethod')
      : props.getValues('investingProfile.paymentMethod')
    // set final payment method before proceeding
    setSelected(paymentMethod)
    props.setPaymentOptionSelected(paymentMethod)
    props.setValue('investingProfile.paymentMethod', paymentMethod)

    if (paymentMethod === PaymentMethodsEnum.WIRE) {
      await handleWirePaymentMethod(accessToken)
    } else if (paymentMethod === PaymentMethodsEnum.CARD) {
      await handleCCPaymentMethod(accessToken)
    } else if (paymentMethod === PaymentMethodsEnum.ACH) {
      await handleACHPaymentMethod(accessToken)
    }
  }

  const handleSubmit = async () => {
    if (!props.getValues('investingProfile.paymentMethod')) {
      notification.warning({
        duration: 2,
        message: 'Payment method is required',
        placement: 'topRight',
        description: 'Select a payment method.',
        key: 'form-notif',
      })
      return
    }

    props.setPaymentOptionSelected(props.getValues('investingProfile.paymentMethod'))
    setIsLoading(true)
    // generate token
    const accessToken = await getFreshBearerToken()
    try {
      await handlePaymentMethod(accessToken)
      setIsLoading(false)
    } catch (error) {
      consoleLog('Submit payment method error: ', error)
      setError(
        'Please check if your details are valid and correct. Check if payment got charge and if not you can retry or you can contact us.',
      )
      setIsLoading(false)
    }
  }

  useEffect(() => {
    props.setValue('paymentIframe', '')
  }, []) // eslint-disable-line

  return (
    <div className="flex h-screen w-full flex-col md:flex-row md:justify-start">
      <div className="mt-10 flex flex-col sm:w-screen md:mt-0 md:min-h-screen md:w-[45%] lg:w-[40%] xl:w-[35%]">
        <div className="relative w-screen px-10 sm:h-auto sm:px-16 md:hidden">
          <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
        </div>
        <div className="relative hidden flex-grow md:flex">
          <img className="flex h-screen w-full" src={Side2} alt={`${Side2}`} />
        </div>
      </div>
      <div className="flex flex-col p-10 sm:w-[100%] md:w-[55%] lg:h-screen lg:w-[60%] lg:overflow-y-scroll xl:w-[65%]">
        {/* <div className="justify-center my-10 hidden md:flex">
          <div className="w-1/2">
            <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
          </div>
        </div> */}

        <div className="md:my-3">
          <ReInvestStepper current={Number(props.currentStep)} />
        </div>

        <div className="py-8 text-left text-2xl font-semibold text-gray-900 md:py-12 md:pb-10 md:text-4xl">
          Would you like to reinvest using the same payment method?
        </div>
        <span className="mb-2 w-full text-start text-base font-normal text-gray-400" hidden={!samePaymentMethod}>
          Your first payment method is{' '}
          <strong className="text-gray-900">{props.getValues('investingProfile.previousPaymentMethod')}</strong>
        </span>

        <div className="mb-2 flex w-full justify-center lg:justify-start">
          <div className="flex w-full justify-center gap-4">
            <div
              className={`flex h-[64px] w-[100%] items-center gap-3 rounded-md border-[1px] px-6 py-4 text-center 
                ${isLoading ? 'cursor-not-allowed opacity-50' : 'hover:cursor-pointer'} ${
                  samePaymentMethod ? 'border-[#1A56DB] bg-[#E8EEFB]' : ''
                }`}
              onClick={() => {
                if (!isLoading) {
                  setSamePaymentMethod(true)
                }
              }}
            >
              <AiFillCheckCircle size={'32px'} className="text-base text-green-600" />
              <div className="text=[#111928] text-sm font-normal">Yes</div>
            </div>
            <div
              className={`flex h-[64px] w-[100%] items-center gap-3 rounded-md border-[1px] px-6 py-4 text-center
                ${isLoading ? 'cursor-not-allowed opacity-50' : 'hover:cursor-pointer'} ${
                  !samePaymentMethod ? 'border-[#1A56DB] bg-[#E8EEFB]' : ''
                }`}
              onClick={() => {
                !isLoading && setSamePaymentMethod(false)
              }}
            >
              <AiFillCloseCircle size={'32px'} className="text-base text-red-500" />
              <div className="text=[#111928] text-sm font-normal">No</div>
            </div>
          </div>
        </div>

        <div className={`grid gap-8 px-12 py-6 md:flex md:px-0 ${samePaymentMethod && '!hidden'}`}>
          <div
            style={{ border: '2px solid #E5E7EB' }}
            className={`flex min-h-[174px] flex-col items-center justify-center rounded-md border-[#E5E7EB] p-7 text-center hover:cursor-pointer sm:border-solid sm:border-[#DDD] md:min-h-[252px] md:w-1/4 md:border-2 ${
              selected === PaymentMethodsEnum.ACH ? 'border-[#1A56DB] bg-[#1A56DB] text-[#FFFFFF] shadow-xl' : ''
            }`}
            onClick={() => {
              if (!isLoading) {
                handlePaymentSelection(PaymentMethodsEnum.ACH)
              }
            }}
          >
            <img src={BankAccountIcon} alt="BankAccountIcon" className="min-w-[92px]" />
            <div className={`mt-4 text-base ${selected === PaymentMethodsEnum.ACH ? 'font-bold' : 'font-normal'}`}>
              From Bank Account
            </div>
          </div>

          <div
            style={{ border: '2px solid #E5E7EB' }}
            className={`flex min-h-[174px] flex-col items-center justify-center rounded-md border-[#E5E7EB] p-7 text-center hover:cursor-pointer sm:border-solid sm:border-[#DDD] md:min-h-[252px] md:w-1/4 md:border-2 ${
              selected === PaymentMethodsEnum.CARD ? 'border-[#1A56DB] bg-[#1A56DB] text-[#FFFFFF] shadow-xl' : ''
            } ${props.getValues('investingProfile.amount') > 5250 ? 'opacity-50' : 'opacity-1'}`}
            onClick={() => {
              if (!isLoading && props.getValues('investingProfile.amount') <= 5250) {
                handlePaymentSelection(PaymentMethodsEnum.CARD)
              }
            }}
          >
            <img src={CreditDebitIcon} alt="CreditDebitIcon" className={` min-w-[92px]`} />
            <div className={`mt-4 text-base ${selected === PaymentMethodsEnum.CARD ? 'font-bold' : 'font-normal'}`}>
              Credit/Debit
            </div>
          </div>

          <div
            style={{ border: '2px solid #E5E7EB' }}
            className={`flex min-h-[174px] flex-col items-center justify-center rounded-md border-[#E5E7EB] p-7 text-center hover:cursor-pointer sm:border-solid sm:border-[#DDD] md:min-h-[252px] md:w-1/4 md:border-2 ${
              selected === PaymentMethodsEnum.WIRE ? 'border-[#1A56DB] bg-[#1A56DB] text-[#FFFFFF] shadow-xl' : ''
            }`}
            onClick={() => {
              if (!isLoading) {
                handlePaymentSelection(PaymentMethodsEnum.WIRE)
              }
            }}
          >
            <img src={BankWireIcon} alt="BankWireIcon" className="min-w-[92px]" />
            <div className={`mt-4 text-base ${selected === PaymentMethodsEnum.WIRE ? 'font-bold' : 'font-normal'}`}>
              Bank Wire
            </div>
          </div>
        </div>
        {isLoading && Number(props.getValues('investingProfile.amount')) > 5250 && (
          <div className="mt-4 text-base leading-5 text-gray-500">Credit Card transactions are limited to $5250.00</div>
        )}
        <div className="w-full justify-center gap-3 pt-10 sm:block md:flex">
          <div className="flex flex-col sm:w-[100%] md:w-[20%]">
            <Button btnName={'Back'} onClickHandler={() => props.previousStep('3')} disabled={isLoading} />
          </div>
          <div className="flex flex-col sm:w-[100%] md:w-[80%]">
            <Button
              btnName={'Next: Process Payment'}
              btnType={'button'}
              onClickHandler={!isLoading && handleSubmit}
              disabled={isLoading}
            />
          </div>
        </div>
        {isLoading && (
          <div className="mt-4 flex gap-3 rounded-md bg-[#FFE5D3] p-4">
            <div className="flex items-center">
              <LuLoader2 className="animate-spin text-4xl" />
            </div>
            <div className="text-sm">
              Connecting to the payment processing screen may take up to 20 seconds. Thank you for your patience!
            </div>
          </div>
        )}
      </div>
      <ErrorMessageModal
        isVisible={error !== ''}
        onProceed={() => {
          setError('')
        }}
        onCancel={() => {}}
        error={error}
        btn2={'Ok'}
      />
    </div>
  )
}

export default PaymentSelection
